<template>
  <!--
    AB#21826 — the Dialog contract (title, modal-content / footer slots,
    save / cancel / close events, loading) rendered through the shared right-side
    Drawer shell, so an order action can leave the centre of the screen without
    a single field, validation or button moving. The order stays visible and
    scrollable beside it.

    Same props and slots as Dialog.vue on purpose: a caller swaps the tag and
    nothing else. Width: `sm` → 460px, default → 560px, `md` → 640px, `lg` →
    760px, or `customWidth`.

    Ticket 9782 — every drawer built on this holds a form, so the shell is told
    so (`form`): an outside click does not close it, and ×, Esc and Cancel ask
    before discarding what was entered.
  -->
  <Drawer
    ref="drawer"
    form
    :title="title"
    :subtitle="subtitle"
    :width="width"
    :data-test="dataTest"
    @close="closeEvent"
  >
    <template v-if="customTitle" #title>
      <slot name="title" />
    </template>

    <div v-if="loading" class="drd-loading" data-test="drd-loading">
      <span class="drd-spinner"></span>
    </div>
    <div v-if="$slots['header-right']" class="drd-header-right">
      <slot name="header-right" />
    </div>
    <slot name="modal-content" />

    <template v-if="showFooter !== false" #footer>
      <template v-if="customFooter">
        <slot name="footer" />
      </template>
      <template v-else>
        <button
          v-if="showCancelButton"
          type="button"
          class="dash-btn"
          data-test="drd-cancel"
          data-drawer-cancel
          @click="cancelEvent"
        >
          {{ cancelButtonText ?? 'Cancel' }}
        </button>
        <button
          v-if="showSaveButton"
          type="button"
          class="dash-btn dash-btn--primary"
          data-test="drd-save"
          v-bind="saveButtonProps"
          :disabled="saveButtonDisabled"
          @click="saveEvent"
        >
          {{ saveButtonText ?? 'Save' }}
        </button>
      </template>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ButtonHTMLAttributes } from 'vue';
import Drawer from '@/components/Shared/Drawer.vue';

const props = defineProps<{
  title: string;
  subtitle?: string | null;
  showCloseButton?: boolean;
  showFooter?: boolean;
  customFooter?: boolean;
  customTitle?: boolean;
  showSaveButton?: boolean;
  showCancelButton?: boolean;
  saveButtonText?: string;
  cancelButtonText?: string;
  md?: boolean;
  sm?: boolean;
  lg?: boolean;
  customWidth?: string;
  saveButtonProps?: ButtonHTMLAttributes;
  saveButtonDisabled?: boolean;
  loading?: boolean;
  dataTest?: string;
}>();

const emit = defineEmits(['closeEvent', 'saveEvent', 'cancelEvent']);

const width = computed(() => {
  if (props.customWidth) return props.customWidth;
  if (props.lg) return '760px';
  if (props.md) return '640px';
  if (props.sm) return '460px';
  return '560px';
});

const drawer = ref<InstanceType<typeof Drawer> | null>(null);

/**
 * For a form that saves and stays open: what was entered is stored, so × and
 * Esc no longer ask before closing.
 */
const markClean = (): void => drawer.value?.markClean();
defineExpose({ markClean });

const cancelEvent = () => emit('cancelEvent');
const closeEvent = () => emit('closeEvent');
const saveEvent = () => emit('saveEvent');
</script>

<style scoped lang="scss">
.drd-loading {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface);
  opacity: 0.85;
}
.drd-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--accent);
  border-right-color: transparent;
  border-radius: 999px;
  animation: drd-spin 0.8s linear infinite;
}
@keyframes drd-spin {
  to {
    transform: rotate(360deg);
  }
}
.drd-header-right {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}
</style>
